if (!customElements.get('ig-new-arrivals')) {
  customElements.define('ig-new-arrivals', class extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.track = this.querySelector('.ig-arrivals__track');
      this.status = this.querySelector('[data-arrivals-status]');
      this.nextButton = this.querySelector('[data-arrivals-next]');
      this.previousButton = this.querySelector('[data-arrivals-previous]');
      this.nextPage = this.dataset.nextPage;
      this.ids = new Set([...this.track.children].map(card => card.dataset.arrivalId));
      this.imageObserver = new IntersectionObserver(entries => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            entry.target.loading = 'eager';
            this.imageObserver.unobserve(entry.target);
          }
        }
      }, { root: this.track, rootMargin: '0px 100% 0px 100%' });
      this.prepareImages(this.track);
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.nextButton.addEventListener('click', () => this.advance(1), options);
      this.previousButton.addEventListener('click', () => this.advance(-1), options);
      this.track.addEventListener('scroll', () => {
        if (this.track.scrollWidth - this.track.scrollLeft - this.track.clientWidth < this.track.clientWidth * 1.5) this.loadNext();
        this.updateControls();
      }, { ...options, passive: true });
      this.addEventListener('mouseenter', () => { this.hovering = true; }, options);
      this.addEventListener('mouseleave', () => { this.hovering = false; }, options);
      this.visibility = new IntersectionObserver(entries => {
        this.inView = entries[0].isIntersecting;
        if (this.inView && this.track.scrollWidth <= this.track.clientWidth + 2) this.loadNext();
      });
      this.visibility.observe(this);
      this.timer = setInterval(() => {
        if (this.dataset.autoplay === 'true' && this.inView && !this.hovering && !document.hidden && !this.contains(document.activeElement) && !matchMedia('(prefers-reduced-motion: reduce)').matches) this.advance(1);
      }, 5000);
      this.updateControls();
    }
    disconnectedCallback() {
      this.events?.abort();
      this.request?.abort();
      this.visibility?.disconnect();
      this.imageObserver?.disconnect();
      clearInterval(this.timer);
      this.ready = false;
    }
    updateControls() {
      const start = this.track.scrollLeft <= 2;
      const end = this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - 2;
      this.previousButton.disabled = start && this.dataset.loop !== 'true';
      this.nextButton.disabled = end && !this.nextPage && this.dataset.loop !== 'true';
    }
    prepareImages(root) {
      for (const image of root.querySelectorAll('img[data-srcset]')) {
        const source = image.getAttribute('data-srcset');
        image.setAttribute('srcset', source);
        image.setAttribute('sizes', '(max-width: 639px) 100vw, (max-width: 1023px) 33vw, 20vw');
        image.src = source.split(',')[0].trim().split(/\s+/)[0];
        image.classList.remove('lazyload');
        image.classList.add('lazyloaded');
        image.decoding = 'async';
        this.imageObserver.observe(image);
      }
    }
    async loadVisibleImages(left) {
      const width = this.track.clientWidth;
      const images = [...this.track.children]
        .filter(card => card.offsetLeft + card.offsetWidth >= left - width && card.offsetLeft <= left + width * 2)
        .flatMap(card => [...card.querySelectorAll('img[srcset]')]);
      await Promise.all(images.map(image => {
        image.loading = 'eager';
        return image.decode().catch(() => {});
      }));
    }
    async loadNext() {
      if (this.pending) return this.pending;
      if (!this.nextPage) return;
      this.pending = this.fetchNext();
      try { await this.pending; } finally { this.pending = null; }
    }
    async fetchNext() {
      this.status.textContent = this.dataset.loading;
      this.setAttribute('aria-busy', 'true');
      this.request = new AbortController();
      try {
        const url = new URL(location.href);
        url.searchParams.delete('sections');
        url.searchParams.set('section_id', this.dataset.sectionId);
        url.searchParams.set(this.dataset.pageParam, this.nextPage);
        const response = await fetch(url, { signal: this.request.signal, credentials: 'same-origin' });
        if (!response.ok) throw new Error('Section request failed');
        const document = new DOMParser().parseFromString(await response.text(), 'text/html');
        const next = [...document.querySelectorAll('ig-new-arrivals')].find(root => root.dataset.sectionId === this.dataset.sectionId);
        if (!next) throw new Error('Section missing');
        for (const card of next.querySelectorAll('.ig-arrivals__item')) {
          if (!this.ids.has(card.dataset.arrivalId)) {
            this.ids.add(card.dataset.arrivalId);
            this.prepareImages(card);
            this.track.append(card);
          }
        }
        this.nextPage = next.dataset.nextPage;
        this.dataset.nextPage = this.nextPage;
        this.status.textContent = '';
        if (window.BoosterTheme?.events?.trigger) window.BoosterTheme.events.trigger('booster:content:update');
      } catch (error) {
        if (error.name !== 'AbortError') this.status.textContent = this.dataset.error;
      } finally {
        this.removeAttribute('aria-busy');
        this.updateControls();
      }
    }
    async advance(direction) {
      if (this.advancing) return;
      this.advancing = true;
      try {
      const width = this.track.clientWidth;
      const end = this.track.scrollWidth - width;
      if (direction > 0 && this.nextPage && end - this.track.scrollLeft < width * 1.5) await this.loadNext();
      let left = this.track.scrollLeft + direction * width;
      const maximum = this.track.scrollWidth - width;
      if (direction > 0 && this.track.scrollLeft >= maximum - 2 && !this.nextPage && this.dataset.loop === 'true') left = 0;
      if (direction < 0 && this.track.scrollLeft <= 2 && this.dataset.loop === 'true') left = maximum;
      await this.loadVisibleImages(Math.max(0, Math.min(left, maximum)));
      this.track.scrollTo({ left: Math.max(0, Math.min(left, maximum)), behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
      this.updateControls();
      } finally {
        this.advancing = false;
      }
    }
  });
}

